// Doctor Service
// Handles all doctor-related API calls and data management

import authService from './Auth.services.js';
import { handleApiRequest, buildQueryParams, createApiHeaders, makeApiRequest } from '../utils/apiResponseHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://the-live-cure-be.vercel.app/api';

class DoctorService {
  // Get all doctors with optional filters
  async getAllDoctors(filters = {}) {
    const token = authService.getToken();
    const queryParams = buildQueryParams(filters);
    const headers = createApiHeaders(token);

    const apiCall = () => makeApiRequest(`${API_BASE_URL}/doctors?${queryParams}`, {
      method: 'GET',
      headers
    });

    return handleApiRequest(apiCall, 'doctors', 'fetch doctors');
  }

  // Get doctor by ID
  async getDoctorById(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor');
      }

      const data = await response.json();
      return data.result || data.doctor || data;
    } catch (error) {
      console.error('Get doctor by ID error:', error);
      throw error;
    }
  }

  // Create new doctor
  async createDoctor(doctorData, profileImageFile = null) {
    try {
      const token = authService.getToken();
      
      let body;
      let headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // If image is provided, use FormData; otherwise use JSON
      if (profileImageFile) {
        const formData = new FormData();
        formData.append('profileImage', profileImageFile);
        
        // Append all other doctor data fields
        Object.keys(doctorData).forEach(key => {
          if (doctorData[key] !== null && doctorData[key] !== undefined) {
            if (Array.isArray(doctorData[key])) {
              // Check if array contains objects (like qualifications)
              const hasObjects = doctorData[key].length > 0 && typeof doctorData[key][0] === 'object';
              
              if (hasObjects) {
                // For arrays of objects (like qualifications), send as JSON string
                formData.append(key, JSON.stringify(doctorData[key]));
              } else {
                // For simple arrays (like cities, diseasesTreated), append each item
                doctorData[key].forEach((item) => {
                  formData.append(`${key}[]`, item);
                });
              }
            } else if (typeof doctorData[key] === 'object') {
              // Handle objects like mapCoordinates, workingHours
              formData.append(key, JSON.stringify(doctorData[key]));
            } else {
              formData.append(key, doctorData[key]);
            }
          }
        });

        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(doctorData);
      }

      const response = await fetch(`${API_BASE_URL}/doctors`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create doctor');
      }

      const data = await response.json();
      return data.result || data.doctor || data;
    } catch (error) {
      console.error('Create doctor error:', error);
      throw error;
    }
  }

  // Update doctor
  async updateDoctor(doctorId, doctorData, profileImageFile = null) {
    try {
      const token = authService.getToken();
      
      let body;
      let headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // If image is provided, use FormData; otherwise use JSON
      if (profileImageFile) {
        const formData = new FormData();
        formData.append('profileImage', profileImageFile);
        
        // Append all other doctor data fields
        Object.keys(doctorData).forEach(key => {
          if (doctorData[key] !== null && doctorData[key] !== undefined) {
            if (Array.isArray(doctorData[key])) {
              // Check if array contains objects (like qualifications)
              const hasObjects = doctorData[key].length > 0 && typeof doctorData[key][0] === 'object';
              
              if (hasObjects) {
                // For arrays of objects (like qualifications), send as JSON string
                formData.append(key, JSON.stringify(doctorData[key]));
              } else {
                // For simple arrays (like cities, diseasesTreated), append each item
                doctorData[key].forEach((item) => {
                  formData.append(`${key}[]`, item);
                });
              }
            } else if (typeof doctorData[key] === 'object') {
              // Handle objects like mapCoordinates, workingHours
              formData.append(key, JSON.stringify(doctorData[key]));
            } else {
              formData.append(key, doctorData[key]);
            }
          }
        });

        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(doctorData);
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}`, {
        method: 'PUT',
        headers,
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update doctor');
      }

      const data = await response.json();
      return data.result || data.doctor || data;
    } catch (error) {
      console.error('Update doctor error:', error);
      throw error;
    }
  }

  // Delete doctor
  async deleteDoctor(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete doctor');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete doctor error:', error);
      throw error;
    }
  }

  // Get doctors by specialty
  async getDoctorsBySpecialty(specialty) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/specialty/${specialty}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctors by specialty');
      }

      const data = await response.json();
      return data.doctors;
    } catch (error) {
      console.error('Get doctors by specialty error:', error);
      throw error;
    }
  }

  // Get doctors by city
  async getDoctorsByCity(city) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/city/${city}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctors by city');
      }

      const data = await response.json();
      return data.doctors;
    } catch (error) {
      console.error('Get doctors by city error:', error);
      throw error;
    }
  }

  // Search doctors
  async searchDoctors(searchTerm) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to search doctors');
      }

      const data = await response.json();
      return data.doctors;
    } catch (error) {
      console.error('Search doctors error:', error);
      throw error;
    }
  }

  // Get doctor statistics
  async getDoctorStats() {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/stats`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor statistics');
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Get doctor stats error:', error);
      throw error;
    }
  }

  // Upload doctor profile image
  async uploadDoctorImage(doctorId, imageFile) {
    try {
      const token = authService.getToken();
      
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/image`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload doctor image');
      }

      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error('Upload doctor image error:', error);
      throw error;
    }
  }

  // Get doctor availability
  async getDoctorAvailability(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor availability');
      }

      const data = await response.json();
      return data.availability;
    } catch (error) {
      console.error('Get doctor availability error:', error);
      throw error;
    }
  }

  // Update doctor availability
  async updateDoctorAvailability(doctorId, availabilityData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(availabilityData),
      });

      if (!response.ok) {
        throw new Error('Failed to update doctor availability');
      }

      const data = await response.json();
      return data.availability;
    } catch (error) {
      console.error('Update doctor availability error:', error);
      throw error;
    }
  }

  // Get doctor reviews
  async getDoctorReviews(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/reviews`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor reviews');
      }

      const data = await response.json();
      return data.reviews;
    } catch (error) {
      console.error('Get doctor reviews error:', error);
      throw error;
    }
  }

  // Add doctor review
  async addDoctorReview(doctorId, reviewData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/reviews`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reviewData),
      });

      if (!response.ok) {
        throw new Error('Failed to add doctor review');
      }

      const data = await response.json();
      return data.review;
    } catch (error) {
      console.error('Add doctor review error:', error);
      throw error;
    }
  }

  // Get doctor specialties
  async getDoctorSpecialties() {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/specialties`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor specialties');
      }

      const data = await response.json();
      return data.specialties;
    } catch (error) {
      console.error('Get doctor specialties error:', error);
      throw error;
    }
  }

  // Get doctor cities
  async getDoctorCities() {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/cities`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor cities');
      }

      const data = await response.json();
      return data.result?.cities || data.cities;
    } catch (error) {
      console.error('Get doctor cities error:', error);
      throw error;
    }
  }

  // Get doctor statistics
  async getDoctorStats() {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/stats`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor stats');
      }

      const data = await response.json();
      return data.result || data.stats;
    } catch (error) {
      console.error('Get doctor stats error:', error);
      throw error;
    }
  }

  // Upload doctor image
  async uploadDoctorImage(doctorId, imageFile) {
    try {
      const token = authService.getToken();
      
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/image`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload doctor image');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Upload doctor image error:', error);
      throw error;
    }
  }

  // Get doctor availability
  async getDoctorAvailability(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor availability');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Get doctor availability error:', error);
      throw error;
    }
  }

  // Update doctor availability
  async updateDoctorAvailability(doctorId, availabilityData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/availability`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(availabilityData),
      });

      if (!response.ok) {
        throw new Error('Failed to update doctor availability');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Update doctor availability error:', error);
      throw error;
    }
  }

  // Get doctor reviews
  async getDoctorReviews(doctorId) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/reviews`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor reviews');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Get doctor reviews error:', error);
      throw error;
    }
  }

  // Add doctor review
  async addDoctorReview(doctorId, reviewData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/reviews`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reviewData),
      });

      if (!response.ok) {
        throw new Error('Failed to add doctor review');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Add doctor review error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const doctorService = new DoctorService();
export default doctorService;
